<template>
  <div class="bb-contact-companies-visas" v-if="companyId && companyId !== 'new'">
    <transition name="fade" appear>
      <loader-fly v-if="loaderRun"/>
    </transition>
    <div class="bb-contact-companies-visas-section">
      <div class="bb-contact-companies-visas__title">Investors</div>
      <div class="bb-contact-companies-visas__table">
        <ContactCompanyVisasTable :columns="investorsColumns"
                                  :checkChangeDates="true"
                                  :rows="investors"
                                  @delete="deleteVisa"
                                  @save="updateTable"/>
      </div>
      <div class="bb-button-dashed bb-contact-companies-visas__button-new"
           :class="{'bb-contact-companies-visas__button-new_disabled': !hasFreeVisas}"
           @click="addNewEntry(visaOwnerType.investor)">
        <template v-if="hasFreeVisas"><i class="icon-cross"/>Add new</template>
        <template v-else>Visa quota limit is reached</template>
      </div>
      <div class="bb-contact-companies-visas__button-expired"
           v-if="investorsCountExp"
           @click="investorsExpiredOpen = !investorsExpiredOpen">
        + {{investorsCountExp}} Cancelled Visa
      </div>
      <transition name="fade">
        <div class="bb-contact-companies-visas__table" v-if="investorsCountExp && investorsExpiredOpen">
          <ContactCompanyVisasTable :columns="investorsColumns"
                                    :checkChangeDates="true"
                                    :rows="investorsExpired"
                                    @delete="deleteVisa"
                                    @save="updateTable"/>
        </div>
      </transition>
    </div>
    <div class="bb-contact-companies-visas-section">
      <div class="bb-contact-companies-visas__title">Employees</div>
      <div class="bb-contact-companies-visas__table">
        <ContactCompanyVisasTable :columns="employeesColumns"
                                  :checkChangeDates="true"
                                  :rows="employees"
                                  @delete="deleteVisa"
                                  @save="updateTable"/>
      </div>
      <div class="bb-button-dashed bb-contact-companies-visas__button-new"
           :class="{'bb-contact-companies-visas__button-new_disabled': !hasFreeVisas}"
           @click="addNewEntry(visaOwnerType.employee)">
        <template v-if="hasFreeVisas"><i class="icon-cross"/>Add new</template>
        <template v-else>Visa quota limit is reached</template>
      </div>
      <div class="bb-contact-companies-visas__button-expired"
           v-if="employeesCountExp"
           @click="employeesExpiredOpen = !employeesExpiredOpen">
        + {{employeesCountExp}} Cancelled Visa
      </div>
      <transition name="fade">
        <div class="bb-contact-companies-visas__table" v-if="employeesCountExp && employeesExpiredOpen">
          <ContactCompanyVisasTable :columns="employeesColumns"
                                    :checkChangeDates="true"
                                    :rows="employeesExpired"
                                    @delete="deleteVisa"
                                    @save="updateTable"/>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import LoaderFly from '@/components/Main/Loaders/LoaderFly'
import Pagination from '@/components/Main/Pagination/Pagination'
import ContactCompanyVisasTable
  from '@/components/Dissemination/Sales/CRM/ContactCompany/ContactCompanyVisas/ContactCompanyVisasTable'
import {contactsVisasTableColumns} from '@/constants/TablesOptions/contactsVisasTable'
import {filter, findIndex, get, trim, find, cloneDeep, round} from 'lodash'
import moment from 'moment'
import {contractType, visaOwnerType, visaStatus} from '@/constants/companyVisas'
import ContactCompanyVisasApi from '@/api/ContactVisas/api'
import ContactCompanyApi from '@/api/ContactCompanies/api'

export default {
  name: 'ContactCompanyVisas',
  components: {
    ContactCompanyVisasTable,
    Pagination,
    LoaderFly
  },
  data () {
    return {
      visaStatus: visaStatus,
      visaOwnerType: visaOwnerType,
      contractType: contractType,
      investors: [],
      investorsExpired: [],
      employees: [],
      employeesExpired: [],
      loaderRun: true,
      investorsExpiredOpen: false,
      employeesExpiredOpen: false,
      totalActiveVisas: 0,
      currentCompany: {}
    }
  },
  computed: {
    investorsCountExp () {
      return this.investorsExpired.length
    },
    employeesCountExp () {
      return this.employeesExpired.length
    },
    hasFreeVisas () {
      const visaQuote = get(this.currentCompany, 'visaQuote', 1)
      return (visaQuote - this.totalActiveVisas) > 0
    },
    investorsColumns () {
      const ownerType = this.visaOwnerType.investor
      return filter(contactsVisasTableColumns, item => !item.ownerType || item.ownerType === ownerType)
    },
    employeesColumns () {
      const ownerType = this.visaOwnerType.employee
      return filter(contactsVisasTableColumns, item => !item.ownerType || item.ownerType === ownerType)
    },
    contactId () {
      return this.$route.params.id
    },
    companyId () {
      return this.$route.params.company
    },
    clientCompanies () {
      return this.$store.getters.clientCompanies
    },
    userInfo () {
      return this.$store.getters.userInfo
    }
  },
  methods: {
    async getVisas ({page, visaOwnerType, visaStatus}) {
      try {
        const resp = await ContactCompanyVisasApi.getVisas({
          page,
          visaOwnerType,
          visaStatus,
          client: this.clientId,
          clientCompany: this.companyId
        })
        return resp.data.clientcompanyvisas.map(item => {
          item.visaStatus = find(this.visaStatus, {id: item.visaStatus}) || {}
          item.contractType = find(this.contractType, {id: item.contractType}) || {}
          return item
        })
      } catch (err) {
        console.error(err)
      }
    },
    getTotalActiveVisas () {
      ContactCompanyVisasApi.getVisas({
        limit: 1,
        visaStatus: '1&visaStatus[]=2',
        client: this.clientId,
        clientCompany: this.companyId
      })
        .then((resp) => {
          this.totalActiveVisas = resp.data.totalDocs
        })
        .catch((err) => {
          console.error(err)
        })
    },
    getCompany () {
      const company = find(this.clientCompanies, {id: this.companyId})
      if (company) {
        this.currentCompany = company
        return
      }
      this.loaderRun = true
      ContactCompanyApi.getContactCompanyById(this.companyId)
        .then((resp) => {
          this.loaderRun = false
          this.currentCompany = resp.data
        })
        .catch((e) => {
          this.loaderRun = false
          console.error(e)
        })
    },
    newEntry (type) {
      const entry = {
        id: 'new',
        visaOwnerType: type,
        visaOwnerName: '',
        visaOwnerSalary: '',
        visaExpDate: new Date(),
        visaStatus: visaStatus[0]
      }
      if (type === this.visaOwnerType.employee) {
        entry.contractType = contractType[0]
        entry.contractDates = {
          start: new Date(),
          end: new Date()
        }
      }
      return entry
    },
    addNewEntry (ownerType) {
      if (!this.hasFreeVisas) return
      const investorsIndex = findIndex(this.investors, {id: 'new'})
      const employeeIndex = findIndex(this.employees, {id: 'new'})
      const newEntry = this.newEntry(ownerType)
      if (ownerType === this.visaOwnerType.investor && investorsIndex === -1) {
        this.investors.unshift(newEntry)
      } else if (ownerType === this.visaOwnerType.employee && employeeIndex === -1) {
        this.employees.unshift(newEntry)
      }
    },
    spliceRow (targetRows, data, id) {
      const index = findIndex(targetRows, {id: id})
      if (index !== -1 && data) {
        targetRows.splice(index, 1, data)
      } else if (index !== -1 && !data) {
        targetRows.splice(index, 1)
      } else {
        targetRows.unshift(data)
      }
      this.investors = this.investors.filter(item => item.visaStatus.id === 1 || item.visaStatus.id === 2)
      this.investorsExpired = this.investorsExpired.filter(item => item.visaStatus.id === 3)
      this.employees = this.employees.filter(item => item.visaStatus.id === 1 || item.visaStatus.id === 2)
      this.employeesExpired = this.employeesExpired.filter(item => item.visaStatus.id === 3)
    },
    deleteVisa (row) {
      const visa = this.generateBody(row)
      const id = get(row, 'id', '')
      this.spliceRow(this.getTargetRows(visa), '', id)
      ContactCompanyVisasApi.deleteVisa(id)
        .then((resp) => {
          this.getTotalActiveVisas()
        })
        .catch((err) => {
          console.error(err)
        })
    },
    updateTable (row, rows) {
      const visa = this.generateBody(row)
      const req = row.id === 'new' ? ContactCompanyVisasApi.createVisa(visa) : ContactCompanyVisasApi.updateVisa(visa)
      req.then((resp) => {
        const oldId = row.id
        const data = cloneDeep(row)
        data.id = resp.data.id
        const oldRow = find(rows, {id: oldId})
        const oldStatus = get(oldRow, 'visaStatus.id', '')
        const ownerType = get(oldRow, 'visaOwnerType', '')
        const oldRows = this.getTargetRows({visaOwnerType: ownerType, visaStatus: oldStatus})
        const index = findIndex(oldRows, {id: oldId})
        this.getTotalActiveVisas()
        if (index !== -1) {
          oldRows.splice(index, 1)
        }
        this.spliceRow(this.getTargetRows(resp.data), data, oldId)
      })
        .catch((err) => {
          console.error(err)
        })
    },
    getStatus (date, status) {
      const dur = moment.duration({ from: new Date(), to: date })
      const days = round(dur.asDays(), 1)
      if (status.id === 3 || days === 0) {
        return status.id
      } else if (days > 0) {
        return 1
      } else if (days < 0) {
        return 2
      }
    },
    generateBody (visa) {
      const salary = +(visa.visaOwnerSalary || 0)
      const result = {
        user: this.userInfo.id,
        client: this.contactId,
        clientCompany: this.companyId,
        visaOwnerType: visa.visaOwnerType,
        visaOwnerSalary: isNaN(salary) ? 0 : salary,
        visaOwnerName: trim(visa.visaOwnerName),
        visaExpDate: visa.visaExpDate || new Date(),
        uploadFile: get(visa, 'uploadFile.id', '')
      }
      result.visaStatus = this.getStatus(result.visaExpDate, visa.visaStatus)
      visa.visaStatus = find(this.visaStatus, {id: result.visaStatus})
      if (!result.uploadFile) {
        delete result.uploadFile
      }
      if (visa.visaOwnerType === visaOwnerType.employee) {
        result.contractType = get(visa, 'contractType.id', 1)
        result.contractDates = get(visa, 'contractDates', {start: new Date(), end: new Date()})
      }
      if (visa.id !== 'new') {
        result.id = visa.id
      }
      const countryId = get(visa, 'citizenship', '')
      if (countryId) {
        result.citizenship = countryId
      }
      return result
    },
    getTargetRows ({visaStatus, visaOwnerType}) {
      const visaActive = visaStatus === 1 || visaStatus === 2
      const visaExpire = visaStatus === 3
      const contactInvestor = visaOwnerType === this.visaOwnerType.investor
      const contactEmployee = visaOwnerType === this.visaOwnerType.employee
      if (visaActive && contactInvestor) {
        return this.investors
      } else if (visaActive && contactEmployee) {
        return this.employees
      } else if (visaExpire && contactInvestor) {
        return this.investorsExpired
      } else if (visaExpire && contactEmployee) {
        return this.employeesExpired
      }
    },
    async getAllVisas () {
      this.loaderRun = true
      const investorType = visaOwnerType.investor
      const employeeType = visaOwnerType.employee
      this.investors = await this.getVisas({visaOwnerType: investorType, visaStatus: '1&visaStatus[]=2'})
      this.investorsExpired = await this.getVisas({visaOwnerType: investorType, visaStatus: '3'})
      this.employees = await this.getVisas({visaOwnerType: employeeType, visaStatus: '1&visaStatus[]=2'})
      this.employeesExpired = await this.getVisas({visaOwnerType: employeeType, visaStatus: '3'})
      this.loaderRun = false
    }
  },
  watch: {
    clientCompanies () {
      this.getCompany()
    }
  },
  beforeMount () {
    if (this.companyId && this.companyId !== 'new') {
      this.getCompany()
      this.getTotalActiveVisas()
      this.getAllVisas()
    }
  }
}
</script>

<style lang="scss" scoped>
  @import "../../../../../../sass/variables";
  @import "../../../../../../sass/mixins";

  .bb-contact-companies-visas {
    margin: 12px 0;
    display: grid;
    grid-gap: 41px;
    transition: 0.3s;

    &__table {
      margin: 0 25px;
    }

    &__title {
      color: $color-accent;
      font-weight: bold;
      font-size: 16px;
      line-height: 19px;
      margin-bottom: 15px;
      margin-left: 25px;
    }

    &__button-expired {
      display: inline-block;
      border: 1.5px solid $color-accent;
      box-sizing: border-box;
      border-radius: 5px;
      padding: 10px 15px;
      font-weight: 600;
      font-size: 13px;
      line-height: 16px;
      color: $color-accent;
      margin: 25px 25px 15px;
      transition: 0.3s;
      cursor: pointer;

      &:hover {
        border: 1.5px solid $color-accent-hover;
        color: $color-accent-hover;
      }
    }

    &__button-new {
      margin: 7px 25px 0;
      width: calc(100% - 50px);

      &_disabled {
        border-color: $color-silver-chalice;
        color: $color-silver-chalice;
        pointer-events: none;
      }
    }

    &-section {
    }
  }

</style>



// WEBPACK FOOTER //
// src/components/Dissemination/Sales/CRM/ContactCompany/ContactCompanyVisas/ContactCompanyVisas.vue